import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PrimaryButton } from '../../components/chrome/Button.js';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell.js';
import { PaymentSheet } from '../../components/payment/PaymentSheet.js';
import { SKU_CATALOG } from '@wds/astro-bot-shared/catalog';
import { useLuckyDashboard, useLuckyDayDetail, type Intention, type SavedLuckyDashboard } from '../../hooks/useLuckyDashboard.js';
import { useSkuInvoice } from '../../hooks/useSkuInvoice.js';
import { HowItWorks } from '../../components/HowItWorks.js';
import { ReportMarkdown } from '../../components/ReportMarkdown.js';
import { useTextResult } from '../../hooks/useTextResult.js';
import { useAuth } from '../../store/auth.js';
import { apiFetch } from '../../lib/api-client.js';
import { getBaselineSkuRunId } from '../../lib/discover-sku-run.js';
import { LuckyObstacles } from './LuckyObstacles.js';
import { HistoryClock } from '../../components/history/HistoryClock.js';
import { ToolHistorySheet } from '../../components/history/HistorySheet.js';
import { useToolHistory } from '../../hooks/useToolHistory.js';
import { ApiError, ensureOk } from '../../lib/api-error.js';
import styles from './Lucky.module.css';

const GOALS: Intention[] = ['love', 'talk', 'money', 'travel', 'career', 'launch', 'luck'];

type LuckySku = 'lucky_14' | 'lucky_30';

function bandClass(band: string): string {
  return band === 'good' ? styles.good : band === 'soso' ? styles.soso : styles.bad;
}

interface SkuRunRow {
  id: string;
  skuCode: string;
  output: unknown;
  createdAt: string;
}

/**
 * Poll GET /api/v1/sku-runs until a run with id !== baselineRunId appears.
 * Returns the new run's id, or null on timeout.
 */
async function pollForNewRunId(
  jwt: string,
  skuCode: string,
  baselineRunId: string | null,
  opts: { timeoutMs?: number; intervalMs?: number } = {},
): Promise<string | null> {
  const timeoutMs = opts.timeoutMs ?? 60_000;
  const intervalMs = opts.intervalMs ?? 2_000;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    try {
      const res = await apiFetch(
        `/api/v1/sku-runs?skuCode=${encodeURIComponent(skuCode)}&limit=1`,
        jwt,
      );
      if (res.ok) {
        const data = (await res.json()) as { runs?: SkuRunRow[] };
        const run = data.runs?.[0] ?? null;
        if (run && run.id !== baselineRunId) {
          return run.id;
        }
      }
    } catch {
      // transient error — keep polling
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return null;
}

export function Lucky(): JSX.Element {
  const { t, i18n } = useTranslation(['flows', 'common']);
  const jwt = useAuth((s) => s.jwt);
  const history = useToolHistory('lucky', false);
  const [showHistory, setShowHistory] = useState(false);
  const [data, setData] = useState<SavedLuckyDashboard | null>(null);
  const intention = data?.intention ?? null;
  const dashboard = useLuckyDashboard();
  const isLoading = dashboard.isPending;
  const isError = dashboard.isError;
  const busyRef = useRef(false);
  const version = useRef(0);
  const [openDate, setOpenDate] = useState<string | null>(null);
  const detail = useLuckyDayDetail();
  const { purchase, redeemCoupon } = useSkuInvoice();
  const [notice, setNotice] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<LuckySku | null>(null);
  const [isPaying, setIsPaying] = useState(false);
  const paidRunId = data?.paidSkuRunId ?? null;

  const textResult = useTextResult(paidRunId ?? '');

  function clearResult(): void {
    version.current++;
    busyRef.current = false;
    setData(null); setOpenDate(null); setNotice(null); setInvoice(null); setShowHistory(false); setIsPaying(false);
    dashboard.reset(); detail.reset();
  }
  useEffect(() => { clearResult(); }, [history.resultId, history.userId]);
  useEffect(() => {
    if (history.resultId && history.detail.data) setData(history.detail.data as unknown as SavedLuckyDashboard);
  }, [history.resultId, history.detail.data]);
  useEffect(() => () => { version.current++; }, []);
  function startNew(): void { clearResult(); history.open(null); }
  function fmtDate(iso: string): string {
    return new Date(iso).toLocaleDateString(i18n.language, { timeZone: data?.timezone ?? 'UTC', weekday: 'short', month: 'short', day: 'numeric' });
  }
  async function pickIntention(g: Intention): Promise<void> {
    if (busyRef.current) return;
    busyRef.current = true; const current = version.current;
    setOpenDate(null); setNotice(null);
    try {
      const saved = await dashboard.mutateAsync(g);
      if (current !== version.current) return;
      history.open(saved.id);
    } catch { if (current === version.current) setNotice(t('lucky.error')); }
    finally { if (current === version.current) busyRef.current = false; }
  }
  async function toggleDay(dateIso: string, band: string): Promise<void> {
    if (band === 'bad' || !data || busyRef.current) return;
    if (openDate === dateIso) { setOpenDate(null); return; }
    setOpenDate(dateIso);
    detail.reset();
    if (data.detailTexts[dateIso]) return;
    busyRef.current = true; const current = version.current;
    try {
      const result = await detail.mutateAsync({ id: data.id, date: dateIso });
      if (current === version.current && result.text) setData(old => old && ({ ...old, detailTexts: { ...old.detailTexts, [dateIso]: result.text! } }));
    } catch { /* The inline detail error offers another explicit click to retry. */ }
    finally { if (current === version.current) busyRef.current = false; }
  }
  async function attachPaidRun(runId: string | null, current: number, id: string): Promise<void> {
    if (!runId || !jwt || current !== version.current) return;
    await ensureOk(await apiFetch(`/api/v1/lucky/results/${encodeURIComponent(id)}/paid-run`, jwt, {
      method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ paidSkuRunId: runId }),
    }));
    if (current === version.current) setData(old => old && ({ ...old, paidSkuRunId: runId }));
  }

  async function doPurchase(sku: LuckySku): Promise<void> {
    if (!intention || !data || busyRef.current) return;
    const current = version.current; const id = data.id;
    busyRef.current = true;
    setIsPaying(true);
    try {
      const baselineRunId = jwt ? await getBaselineSkuRunId(jwt, sku) : null;
      const { status } = await purchase(sku, { intention });
      if (status === 'paid') {
        setInvoice(null);
        if (jwt) {
          const runId = await pollForNewRunId(jwt, sku, baselineRunId);
          await attachPaidRun(runId, current, id);
        }
      }
    } catch {
      if (current === version.current) setNotice(t('lucky.error'));
    } finally {
      if (current === version.current) { busyRef.current = false; setIsPaying(false); }
    }
  }

  async function doRedeemCoupon(sku: LuckySku, code: string): Promise<void> {
    if (!intention || !data || busyRef.current) throw new Error('no_intention');
    const current = version.current; const id = data.id;
    busyRef.current = true; setIsPaying(true);
    try {
      const baselineRunId = jwt ? await getBaselineSkuRunId(jwt, sku) : null;
      await redeemCoupon(sku, code, { intention });
      if (current !== version.current) return;
      setInvoice(null);
      if (jwt) {
        const runId = await pollForNewRunId(jwt, sku, baselineRunId);
        await attachPaidRun(runId, current, id);
      }
    } finally {
      if (current === version.current) { busyRef.current = false; setIsPaying(false); }
    }
  }

  return (
    <MeSubpageShell title={t('lucky.title')} data-testid="lucky-page" right={<HistoryClock label={t('history.open')} testId="lucky-history" disabled={isLoading || detail.isPending || isPaying} onClick={() => setShowHistory(true)} />}>
      <div className={styles.body}>
        <HowItWorks toolKey="lucky" />
        {history.resultId !== null && history.detail.isPending && <p role="status">{t('common:states.loading')}</p>}
        {history.resultId !== null && history.detail.isError && <div>
          <p role="alert">{t(history.detail.error instanceof ApiError && history.detail.error.status === 404 ? 'history.notFound' : 'history.loadError')}</p>
          <PrimaryButton onClick={() => void history.detail.refetch()}>{t('history.retry')}</PrimaryButton>
          <PrimaryButton onClick={startNew}>{t('history.new')}</PrimaryButton>
        </div>}
        {intention === null && history.resultId === null && (
          <>
            <p className={styles.prompt}>{t('lucky.pickGoal')}</p>
            <div className={styles.goals}>
              {GOALS.map((g) => (
                <button key={g} className={styles.goal} disabled={isLoading} onClick={() => void pickIntention(g)} data-testid={`lucky-goal-${g}`}>
                  {t(`lucky.goal.${g}`)}
                </button>
              ))}
            </div>
          </>
        )}

        {isLoading && <p data-testid="lucky-loading">{t('lucky.loading')}</p>}
        {isError && <p className={styles.error} role="alert">{t('lucky.noChart')}</p>}

        {intention !== null && data && !history.detail.isError && (
          <>
            <p className={styles.prompt} data-testid="lucky-period">{t('lucky.savedPeriod', { topic: t(`lucky.goal.${intention}`), from: new Date(data.periodStart).toLocaleDateString(i18n.language, { timeZone: data.timezone, year: 'numeric', month: 'short', day: 'numeric' }), to: fmtDate(data.days.at(-1)?.date ?? data.periodStart), timezone: data.timezone })}</p>
            <div className={styles.days} data-testid="lucky-days">
              {data.days.map((d) => {
                const clickable = d.metrics.band !== 'bad';
                const isOpen = openDate === d.date;
                return (
                  <div key={d.date} className={styles.dayWrap}>
                    <button
                      type="button"
                      className={styles.day}
                      onClick={() => void toggleDay(d.date, d.metrics.band)}
                      disabled={!clickable || detail.isPending || isPaying}
                      aria-expanded={clickable ? isOpen : undefined}
                      data-testid={`lucky-day-${d.date}`}
                    >
                      <span className={styles.dayDate}>{fmtDate(d.date)}</span>
                      <span className={`${styles.dayScore} ${bandClass(d.metrics.band)}`}>
                        {d.metrics.overall}{t('lucky.scoreSuffix')}
                        {clickable && <span className={styles.chev} aria-hidden>{isOpen ? '▾' : '▸'}</span>}
                      </span>
                    </button>
                    {isOpen && (
                      <div className={styles.detail} data-testid="lucky-detail">
                        <p className={styles.metricLine}>
                          ⚡ {d.metrics.energy} · 🍀 {d.metrics.luck} · 🎨 {d.metrics.creativity} · 🛡️ {d.metrics.smoothness}
                        </p>
                        <LuckyObstacles flags={d.metrics.obstacles} />
                        {detail.isPending && <p className={styles.detailMuted}>{t('lucky.detailLoading')}</p>}
                        {detail.isError && <p className={styles.error} role="alert">{t('lucky.detailError')}</p>}
                        {!detail.isPending && !detail.isError && data.detailTexts[d.date] && (
                          <ReportMarkdown markdown={data.detailTexts[d.date]} />
                        )}
                        {!detail.isPending && !detail.isError && !data.detailTexts[d.date] && (
                          <p className={styles.detailMuted}>{t('lucky.detailError')}</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Inline paid result (self-containment: replaces "sent to chat" notice) */}
            {paidRunId && (
              <div className={styles.paidResult} data-testid="lucky-paid-result">
                {textResult.isLoading && <p className={styles.detailMuted}>{t('lucky.loading')}</p>}
                {textResult.isError && <p className={styles.error} role="alert">{t('lucky.error')}</p>}
                {textResult.data && <ReportMarkdown markdown={textResult.data.markdown} />}
              </div>
            )}

            {/* Show error notice (non-payment errors) only when no paid result is shown */}
            {notice && !paidRunId && (
              <p className={styles.notice} data-testid="lucky-notice">{notice}</p>
            )}

            {/* CTA buttons hidden once a paid result is available */}
            {!paidRunId && !notice && (
              <div className={styles.ctaRow}>
                <PrimaryButton disabled={detail.isPending || isPaying} onClick={() => setInvoice('lucky_14')} data-testid="lucky-buy-14">{t('lucky.more14', { stars: SKU_CATALOG['lucky_14'].stars })}</PrimaryButton>
                <PrimaryButton disabled={detail.isPending || isPaying} onClick={() => setInvoice('lucky_30')} data-testid="lucky-buy-30">{t('lucky.more30', { stars: SKU_CATALOG['lucky_30'].stars })}</PrimaryButton>
              </div>
            )}
            <PrimaryButton onClick={startNew} disabled={detail.isPending || isPaying}>{t('history.new')}</PrimaryButton>
          </>
        )}
        {notice && !data && <p role="alert">{notice}</p>}
      </div>

      {showHistory && <ToolHistorySheet kind="lucky" currentId={data?.id ?? history.resultId} onClose={() => setShowHistory(false)} onOpen={id => { setShowHistory(false); history.open(id); }} onDeleted={id => { if (id === data?.id || id === history.resultId) startNew(); }} />}
      {invoice && (
        <PaymentSheet
          skuCode={invoice}
          title={t(invoice === 'lucky_14' ? 'lucky.more14' : 'lucky.more30', { stars: SKU_CATALOG[invoice].stars })}
          priceStars={SKU_CATALOG[invoice].stars}
          bullets={[]}
          isPaying={isPaying}
          onPay={() => void doPurchase(invoice)}
          onCancel={() => setInvoice(null)}
          onApplyCoupon={(code) => doRedeemCoupon(invoice, code)}
        />
      )}
    </MeSubpageShell>
  );
}
